import { Clock, Mail, Plus, Trash2, UserCheck, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useTenant } from "@/contexts/TenantContext";
import { apiClient } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

interface TeamMember {
	id: string;
	userId: string;
	email: string;
	name: string;
	role: string;
	status: string;
	invitedAt?: string;
	joinedAt?: string;
}

export function TeamPage() {
	const { tenant } = useTenant();
	const [members, setMembers] = useState<TeamMember[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [showInviteDialog, setShowInviteDialog] = useState(false);
	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteRole, setInviteRole] = useState("developer");
	const [isSending, setIsSending] = useState(false);

	const loadTeamMembers = async () => {
		if (!tenant) return;

		try {
			setIsLoading(true);
			const data = await apiClient.listTeamMembers(tenant.slug);
			setMembers((data as any) || []);
		} catch (error) {
			console.error("Failed to load team members:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		loadTeamMembers();
	}, [loadTeamMembers]);

	const handleInvite = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!tenant || !inviteEmail) return;

		try {
			setIsSending(true);
			await apiClient.inviteTeamMember(tenant.slug, {
				email: inviteEmail,
				role: inviteRole as "admin" | "member" | "viewer",
			});

			// Reload members
			await loadTeamMembers();

			// Reset form
			setInviteEmail("");
			setInviteRole("developer");
			setShowInviteDialog(false);
		} catch (error) {
			console.error("Failed to send invitation:", error);
		} finally {
			setIsSending(false);
		}
	};

	const handleRemoveMember = async (memberId: string) => {
		if (!tenant) return;
		if (!confirm("Are you sure you want to remove this team member?")) return;

		try {
			await apiClient.removeTeamMember(tenant.id, memberId);
			setMembers(members.filter((m) => m.id !== memberId));
		} catch (error) {
			console.error("Failed to remove member:", error);
		}
	};

	const handleUpdateRole = async (memberId: string, newRole: string) => {
		if (!tenant) return;

		try {
			await apiClient.updateTeamMemberRole(tenant.slug, memberId, {
				role: newRole as "admin" | "member" | "viewer",
			});
			setMembers(
				members.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)),
			);
		} catch (error) {
			console.error("Failed to update role:", error);
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "active":
				return <UserCheck className="w-4 h-4 text-green-500" />;
			case "invited":
				return <Mail className="w-4 h-4 text-blue-500" />;
			case "pending":
				return <Clock className="w-4 h-4 text-orange-500" />;
			default:
				return null;
		}
	};

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
					<p className="text-gray-500 mt-1">Loading team members...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
					<p className="text-gray-500 mt-1">
						Manage team members, roles, and permissions
					</p>
				</div>
				<Button onClick={() => setShowInviteDialog(true)}>
					<Plus className="w-4 h-4 mr-2" />
					Invite Member
				</Button>
			</div>

			{/* Team stats */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-600">Total Members</p>
								<p className="text-2xl font-bold mt-1">{members.length}</p>
							</div>
							<Users className="w-8 h-8 text-blue-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-600">Active Members</p>
								<p className="text-2xl font-bold mt-1">
									{members.filter((m) => m.status === "active").length}
								</p>
							</div>
							<UserCheck className="w-8 h-8 text-green-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-600">Pending Invites</p>
								<p className="text-2xl font-bold mt-1">
									{members.filter((m) => m.status === "invited").length}
								</p>
							</div>
							<Mail className="w-8 h-8 text-orange-500" />
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Team members list */}
			<Card>
				<CardHeader>
					<CardTitle>Team Members</CardTitle>
					<CardDescription>
						Manage your team members and their access levels
					</CardDescription>
				</CardHeader>
				<CardContent>
					{members.length === 0 ? (
						<div className="text-center py-8">
							<Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								No team members yet
							</h3>
							<p className="text-gray-500 mb-4">
								Get started by inviting your first team member
							</p>
							<Button onClick={() => setShowInviteDialog(true)}>
								<Plus className="w-4 h-4 mr-2" />
								Invite Team Member
							</Button>
						</div>
					) : (
						<div className="space-y-3">
							{members.map((member) => (
								<div
									key={member.id}
									className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
								>
									<div className="flex items-center space-x-4">
										{/* Avatar */}
										<div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-medium">
											{member.name
												.split(" ")
												.map((n) => n[0])
												.join("")
												.toUpperCase()}
										</div>

										{/* Info */}
										<div>
											<div className="flex items-center space-x-2">
												<p className="font-medium text-gray-900">
													{member.name}
												</p>
												{getStatusIcon(member.status)}
											</div>
											<p className="text-sm text-gray-500">{member.email}</p>
											{member.joinedAt && (
												<p className="text-xs text-gray-400">
													Joined {formatDateTime(member.joinedAt)}
												</p>
											)}
											{member.invitedAt && member.status === "invited" && (
												<p className="text-xs text-gray-400">
													Invited {formatDateTime(member.invitedAt)}
												</p>
											)}
										</div>
									</div>

									{/* Role and actions */}
									<div className="flex items-center space-x-3">
										<Select
											value={member.role}
											onValueChange={(value) =>
												handleUpdateRole(member.id, value)
											}
											disabled={member.role === "owner"}
										>
											<SelectTrigger className="w-32">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="owner">Owner</SelectItem>
												<SelectItem value="admin">Admin</SelectItem>
												<SelectItem value="developer">Developer</SelectItem>
												<SelectItem value="viewer">Viewer</SelectItem>
											</SelectContent>
										</Select>

										{member.role !== "owner" && (
											<Button
												variant="ghost"
												size="sm"
												onClick={() => handleRemoveMember(member.id)}
											>
												<Trash2 className="w-4 h-4 text-red-500" />
											</Button>
										)}
									</div>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Invite dialog */}
			<Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Invite Team Member</DialogTitle>
						<DialogDescription>
							Send an invitation to add a new member to your team
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleInvite} className="space-y-4">
						<div>
							<Label htmlFor="email">Email address</Label>
							<Input
								id="email"
								type="email"
								placeholder="member@company.com"
								value={inviteEmail}
								onChange={(e) => setInviteEmail(e.target.value)}
								required
							/>
						</div>
						<div>
							<Label htmlFor="role">Role</Label>
							<Select value={inviteRole} onValueChange={setInviteRole}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="admin">Admin</SelectItem>
									<SelectItem value="developer">Developer</SelectItem>
									<SelectItem value="viewer">Viewer</SelectItem>
								</SelectContent>
							</Select>
							<p className="text-xs text-gray-500 mt-1">
								{inviteRole === "admin" &&
									"Full access to all tenant resources"}
								{inviteRole === "developer" &&
									"Can publish and manage packages"}
								{inviteRole === "viewer" && "Read-only access to packages"}
							</p>
						</div>
						<div className="flex justify-end space-x-2">
							<Button
								type="button"
								variant="outline"
								onClick={() => setShowInviteDialog(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isSending}>
								{isSending ? (
									<div className="flex items-center space-x-2">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
										<span>Sending...</span>
									</div>
								) : (
									<>
										<Mail className="w-4 h-4 mr-2" />
										Send Invitation
									</>
								)}
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
